import React, { FC } from 'react';
import { SectionTitle } from '../shared';
import styled, { keyframes } from 'styled-components';
import FULLSCOPE from '../../assets/FullScope.png';
import AIR_LOGO from '../../assets/AIR_Logo.png';
import SAMSON from '../../assets/Samson.png';
import WLIW from '../../assets/wliwFM.png';
import PODFEST from '../../assets/PODFEST.png';
import LIBSYN from '../../assets/libsynlogo.png';
import ONAIR from '../../assets/On_Air_Logo.png';
import RIVERSIDE from '../../assets/RiversideLogo.svg';
import PODCAST_MOVEMENT from '../../assets/PodcastMovement.webp';
import SOUNDS_PROFITABLE from '../../assets/SoundsProfitable.webp';

const SPONSORS = [
  { href: 'https://libsyn.com/', src: LIBSYN, alt: 'Libsyn Logo' },
  { href: 'http://www.samsontech.com/', src: SAMSON, alt: 'Samson Logo' },
  { href: 'https://www.descript.com/', src: FULLSCOPE, alt: 'FullScope Logo' },
  { href: 'https://www.onairfest.com/', src: ONAIR, alt: 'On Air Fest Logo' },
  { href: 'https://www.wliw.org/radio/', src: WLIW, alt: 'WLIWFM Logo' },
  { href: 'https://podcastmovement.com/', src: PODCAST_MOVEMENT, alt: 'Podcast Movement Logo' },
  { href: 'https://podfestexpo.com', src: PODFEST, alt: 'Podfest Logo' },
  { href: 'https://airmedia.org/', src: AIR_LOGO, alt: 'AIR Logo' },
  { href: 'https://creators.riverside.com/Incubator', src: RIVERSIDE, alt: 'Riverside Logo' },
  { href: 'https://soundsprofitable.com/', src: SOUNDS_PROFITABLE, alt: 'Sounds Profitable Logo' },
];

const Section = styled.section`
  margin-top: 4rem;
  padding: 1.5rem 0 4rem;
`;

// ===============
// Perks panel
// ===============
const PerksPanel = styled.div`
  max-width: 960px;
  margin: 1rem auto 3rem;
  padding: 2rem;
  background: linear-gradient(62deg, #6b000d 0%, #990000 100%);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(107, 0, 13, 0.25);
  color: #fff;

  .panel-title {
    font-size: clamp(1.25rem, 2.2vw, 1.6rem);
    font-weight: 800;
    letter-spacing: 0.01em;
    line-height: 1.3;
    margin: 0 0 1.5rem;
    text-align: center;
  }

  .perks {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem 1.25rem;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .perks li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 10px;
    padding: 0.7rem 1rem;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
    transition: background 0.2s ease, transform 0.2s ease;
  }

  .perks li:hover {
    background: rgba(255, 255, 255, 0.16);
    transform: translateY(-2px);
  }

  .perks li::before {
    content: '✓';
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: #fff;
    color: #990000;
    font-weight: 800;
    font-size: 0.85rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
    margin-left: 1rem;
    margin-right: 1rem;
    .perks { grid-template-columns: 1fr; }
  }
`;

// ===============
// Logo marquee
// ===============
const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const Marquee = styled.div`
  position: relative;
  overflow: hidden;
  padding: 1.5rem 0;

  /* Fade the strip out at both edges */
  mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
`;

const Track = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
  animation: ${scroll} 35s linear infinite;

  ${Marquee}:hover & {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    width: 100%;
    justify-content: center;
  }
`;

const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 2.5rem;

  img {
    height: 64px;
    width: auto;
    max-width: 240px;
    object-fit: contain;
    transition: transform 0.25s ease;
  }

  &:hover img {
    transform: scale(1.12);
  }

  @media (max-width: 768px) {
    padding: 0 1.5rem;
    img { height: 48px; max-width: 180px; }
  }
`;

const Affiliates: FC = () => {
  return (
    <Section>
      <SectionTitle color="black">
        Enjoy Perks from Our Sponsors
      </SectionTitle>
      <PerksPanel>
        <p className="panel-title">Our sponsors power your podcast journey</p>
        <ul className="perks">
          <li>Job opportunities</li>
          <li>Access to Stony Brook recording studios</li>
          <li>One-on-one technical instruction</li>
          <li>Sponsor-provided tools</li>
          <li>Invitations to industry events</li>
          <li>Publishing opportunities</li>
          <li>Career support via our active alumni network</li>
          <li>.edu discounts across key tools and platforms</li>
        </ul>
      </PerksPanel>
      <Marquee>
        <Track>
          {/* Track is rendered twice so the loop is seamless */}
          {[false, true].map((clone) => (
            <React.Fragment key={clone ? 'clone' : 'primary'}>
              {SPONSORS.map(({ href, src, alt }) => (
                <LogoLink
                  key={`${alt}${clone ? '-clone' : ''}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-hidden={clone || undefined}
                  tabIndex={clone ? -1 : undefined}
                >
                  <img src={src} alt={clone ? '' : alt} loading="lazy" />
                </LogoLink>
              ))}
            </React.Fragment>
          ))}
        </Track>
      </Marquee>
    </Section>
  );
};

export default Affiliates;
